class AuraFloatingHub extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.isAnimating = false;
    this.toggleButton = null;
    this.items = [];
    this.boundDocumentClick = this.handleDocumentClick.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.toggleButton = this.querySelector('[data-aura-hub-toggle]');
    this.items = Array.from(this.querySelectorAll('[data-aura-hub-item]'));

    if (!this.toggleButton) return;

    this.toggleButton.addEventListener('click', () => this.isOpen ? this.close() : this.open());

    this.querySelectorAll('[data-aura-hub-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.items.forEach((item) => {
      item.addEventListener('click', (event) => this.handleItemClick(event, item));
    });

    document.addEventListener('keydown', this.boundKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundDocumentClick);
    document.removeEventListener('keydown', this.boundKeydown);
  }

  get animationDuration() {
    const value = getComputedStyle(this).getPropertyValue('--aura-hub-animation').trim();
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 360;
  }

  get navigationDelay() {
    const parsed = parseInt(this.dataset.navigationDelay || '220', 10);
    return Number.isFinite(parsed) ? parsed : 220;
  }

  open() {
    if (this.isAnimating || this.isOpen) return;
    this.isAnimating = true;
    this.isOpen = true;
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    this.toggleButton.setAttribute('aria-expanded', 'true');

    window.setTimeout(() => {
      this.isAnimating = false;
    }, this.animationDuration);

    if (String(this.dataset.closeOnOutside) !== 'false') {
      window.setTimeout(() => document.addEventListener('click', this.boundDocumentClick), 0);
    }
  }

  close(callback) {
    if (!this.isOpen && !this.classList.contains('is-open')) {
      if (typeof callback === 'function') callback();
      return;
    }

    this.isAnimating = true;
    this.isOpen = false;
    this.classList.add('is-closing');
    this.classList.remove('is-open');
    this.toggleButton.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.boundDocumentClick);

    window.setTimeout(() => {
      this.classList.remove('is-closing');
      this.isAnimating = false;
      if (typeof callback === 'function') callback();
    }, Math.max(this.animationDuration - 80, 120));
  }

  handleDocumentClick(event) {
    if (this.isOpen && !this.contains(event.target)) this.close();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      this.toggleButton.focus();
    }
  }

  handleItemClick(event, item) {
    const closeAfterClick = String(this.dataset.closeAfterClick) !== 'false';
    const isSearch = item.hasAttribute('data-aura-hub-search');

    if (isSearch) {
      event.preventDefault();
      const openSearch = () => this.openSearchPopup();
      closeAfterClick ? this.close(() => window.setTimeout(openSearch, 40)) : openSearch();
      return;
    }

    if (!closeAfterClick) return;

    const href = item.getAttribute('href');
    if (!href || href === '#') {
      this.close();
      return;
    }

    event.preventDefault();
    this.close(() => {
      window.setTimeout(() => {
        window.location.href = href;
      }, this.navigationDelay);
    });
  }

  openSearchPopup() {
    const selectors = [
      '[data-open-search-popup]',
      '[data-open-search]',
      '.m-header__search',
      '.sf__search-trigger',
      'button[aria-label*="Search"]',
      'a[href="/search"]'
    ];

    for (const selector of selectors) {
      const trigger = document.querySelector(selector);
      if (!trigger || this.contains(trigger)) continue;
      try {
        trigger.click();
        return;
      } catch (error) {}
    }

    window.location.href = '/search';
  }
}

if (!customElements.get('aura-floating-hub')) {
  customElements.define('aura-floating-hub', AuraFloatingHub);
}
